// =============================================================================
// ui/share/sharedTradeRows.ts — a payload trade, in the shape the app's table
// renders.
// -----------------------------------------------------------------------------
// The shared report draws its trades with `TradeTable`, the same component the
// Day Overview uses, so the two cannot drift. That component takes
// `TradeTableRow` — the projection the signed-in app reads from the database —
// and a payload trade is a different, narrower thing: every id in it was already
// resolved to a label at share time, and several columns' worth of fields were
// deliberately never published.
//
// ── ⚠ THIS ADAPTER IS NOT A `Trade`, AND MUST NEVER BECOME ONE ──
//
// It is display scaffolding: enough fields for the cells to render, with the
// ones that never travel — the fill tape, voice notes, dollar risk, the raw
// broker string — absent, as they are in the payload. Nothing here is ever
// written back; a visitor has no session to write with.
//
// ── ⚠ WHY THE FOUR LOOKUPS ──
//
// `TradeTable` resolves the account, the pattern and the trade's value through
// FUNCTIONS, because the signed-in app passes registries and a contract
// catalogue. A visitor has neither. Every one of those was resolved on the
// sharer's machine, so the lookups here just hand back what was stored — keyed
// by id rather than threaded onto the row, so the row shape stays honest about
// what a `TradeTableRow` actually holds.
// =============================================================================

import type { TradeTableRow } from '../../io/sync/trades';
import { TRADE_TABLE_COLUMNS, type TradeColumn } from '../tabs/tradeColumns';
import { maskAccountDigits, type ShareTrade } from '../../selectors/share';

export interface SharedTradeRows {
  rows: TradeTableRow[];
  /** The trade's value in the sharer's display unit. */
  valueById: Map<string, number | null>;
  /** The masked account label. */
  accountById: Map<string, string>;
  /** The pattern NAME, resolved at share time. */
  patternById: Map<string, string>;
  /** Where each id sits in the list, for the modal's ‹ › walk. */
  indexById: Map<string, number>;
}

export function sharedTradeRows(trades: readonly ShareTrade[]): SharedTradeRows {
  const valueById = new Map<string, number | null>();
  const accountById = new Map<string, string>();
  const patternById = new Map<string, string>();
  const indexById = new Map<string, number>();

  const rows = trades.map((t, i) => {
    const id = String(t.id || i);
    valueById.set(id, t.value ?? t.netPnl ?? null);
    // ⚠ Masked again here, for the same reason the header is: a payload
    // written by an older client — or by any future one that forgets — heals
    // rather than publishing a number. Idempotent.
    accountById.set(id, maskAccountDigits(t.account));
    patternById.set(id, t.pattern);
    indexById.set(id, i);

    const row = {
      id,
      // ⚠ `sessionDate: null` and the table asked for 'calendar', so the date
      // renders exactly as stored. The sharer's day-boundary rule was applied
      // at capture time; applying it again would move an evening trade twice.
      date: t.date,
      sessionDate: null,
      timeIn: t.timeIn || null,
      timeOut: t.timeOut || null,
      contract: t.contract,
      side: t.side,
      qty: t.qty,
      entry: t.entry,
      exit: t.exit,
      points: t.points,
      rrRealized: t.rrRealized,
      rrPlanned: t.rrPlanned,
      riskPoints: t.riskPoints,
      maePoints: t.maePoints,
      mfePoints: t.mfePoints,
      grossPnl: t.grossPnl,
      fees: t.fees ?? 0,
      feesAuto: false,
      netPnl: t.netPnl,
      annotated: t.reviewed,
      // 🔴 NULL, ALWAYS. The account cell puts this in a `title` attribute, and
      // the raw broker string is one of the four things that never leave the
      // sharer's machine. It is not in the payload to put here.
      sourceAccount: null,
      syntheticClose: t.syntheticClose,
      patternId: null,
      exitModelId: null,
      sessionLabel: t.session,
      grade: t.grade,
      notes: '',
      mistakeTags: [...t.mistakeTags],
      // ⚠ EMPTY, and that is why the shared table must not carry a Disc.
      // column: the payload publishes the flags as resolved LABELS, not as the
      // keys this map would need. See the header-parity test.
      booleans: {},
      confluences: {},
      mfeSecs: null,
      maeSecs: null,
      accountId: null,
    };
    return row as unknown as TradeTableRow;
  });

  return { rows, valueById, accountById, patternById, indexById };
}

// ---------------------------------------------------------------------------
// Which columns this payload can actually feed
// ---------------------------------------------------------------------------

/**
 * ── 🔴 A SHARE IS A FROZEN SNAPSHOT, AND THAT IS THE WHOLE FRAGILITY ──
 *
 * The viewer has no session, so it cannot read the database — and it must not:
 * a link sent on Tuesday has to show Tuesday's report, not whatever the numbers
 * look like when a stranger opens it. So the payload carries a COPY of the
 * fields the table needs, and any column added later has nothing to read on
 * every link already sent.
 *
 * That much is inherent. What is NOT, and what this exists to stop, is the gap
 * rendering as a confident number: `TradeTable`'s Commissions cell prints
 * `t.fees ?? 0`, which is right in the signed-in app — a zero-commission trade
 * is real — and a lie on a payload that never carried the field.
 *
 * Josh saw it immediately: **"Commissions all show zeros. The Day Overview does
 * not."** His link predated the field by an hour.
 *
 * ▶ So a column whose data this payload cannot supply is DROPPED, and the view
 * says which. A narrower table that is true beats a complete one that invents a
 * figure — the same rule as "absent is not zero" everywhere else here.
 *
 * ⚠ DETECTED FROM THE DATA, not from a version number. A stored `fees` of 0 is
 * a number and reads as present; only a field that was never written is null on
 * EVERY row. A version field would have to be remembered by whoever adds the
 * next column; this does not.
 */
const OPTIONAL_COLUMNS: readonly { key: string; label: string; of: (t: ShareTrade) => unknown }[] = [
  { key: 'fees', label: 'Commissions', of: (t) => t.fees },
  { key: 'grossPnl', label: 'Gross P&L', of: (t) => t.grossPnl },
];

export interface SharedTableLayout {
  columns: readonly TradeColumn[];
  /** Labels of the columns this link is too old to carry. Usually empty. */
  missing: string[];
}

export function sharedTableColumns(trades: readonly ShareTrade[]): SharedTableLayout {
  const missing: string[] = [];
  const drop = new Set<string>();
  for (const c of OPTIONAL_COLUMNS) {
    // ⚠ `some`, not `every`: one trade carrying the field proves the payload has
    // it, and the column then renders honestly for the rest.
    if (!trades.some((t) => c.of(t) != null)) {
      drop.add(c.key);
      missing.push(c.label);
    }
  }
  return { columns: TRADE_TABLE_COLUMNS.filter((c) => !drop.has(c.key)), missing };
}
